import { Link } from '@tanstack/react-router';
import { type FC, type PropsWithChildren, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import { DISCORD_INVITE_URL, FURALITY_MAIL_TO_HELP } from '@/lib/constants.ts';
import { SupportModalContext } from '@/lib/hooks/useSupportModal.ts';
import { helpReport } from '@/lib/troubleshooting.ts';

type SupportModalProps = { show: boolean; setShow: (show: boolean) => void };
const SupportModal: FC<SupportModalProps> = ({ show, setShow }) => {
  const { t } = useTranslation();
  return (
    <Modal show={show} centered onHide={() => setShow(false)}>
      <Modal.Header closeButton>
        <Modal.Title className="h5">{t('shared.footer.support_modal.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Trans
          i18nKey="shared.footer.support_modal.content"
          components={{
            furalityHelpForum: (
              <a href="https://help.furality.org/" target="_blank" rel="noreferrer" />
            ),
            email: <a href={FURALITY_MAIL_TO_HELP} />,
            furalityDiscordServer: <a href={DISCORD_INVITE_URL} target="_blank" rel="noreferrer" />,
            ul: <ul />,
            li: <li />,
            p: <p />,
            safetyForm: <Link to="/safety" />,
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button className="d-block" onClick={helpReport} variant="outline-warning">
          Troubleshooting report
        </Button>
        <Button variant="secondary" onClick={() => setShow(false)}>
          {t('shared.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Allows you to open the Support Modal from anywhere.
 */
const SupportModalProvider: FC<PropsWithChildren> = ({ children }) => {
  const [showSupportModal, setShowSupportModal] = useState(false);
  return (
    <SupportModalContext.Provider value={[showSupportModal, setShowSupportModal]}>
      <SupportModal show={showSupportModal} setShow={setShowSupportModal} />
      {children}
    </SupportModalContext.Provider>
  );
};

export default SupportModalProvider;
